import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useRepoParams } from '../../hooks/useRepoParams';
import { repoApi } from '../../api/client';
import './CommitHistory.css';

interface Commit {
    sha: string;
    message: string;
    author: {
        name: string;
        email: string;
        date: string;
    };
    committer: {
        name: string;
        email: string;
        date: string;
    };
    parents: string[];
}

interface CommitHistoryPageProps {
    repoPath?: string;
    refName?: string;
}

export function CommitHistoryPage({ repoPath: propRepoPath, refName: propRefName }: CommitHistoryPageProps = {}) {
    const { owner, repo } = useRepoParams();
    // Prefer props, fallback to hooks
    const repoPath = propRepoPath || (owner && repo ? `${owner}/${repo}` : '');

    // For wildcard router usage, we must rely on props for refName because params['*'] is the full path
    const ref = propRefName || useParams()['*'] || 'main';

    const [commits, setCommits] = useState<Commit[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        loadCommits();
    }, [ref, page]);

    const loadCommits = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await repoApi.getCommits(repoPath, ref, page) as { commits: Commit[]; hasMore: boolean };
            if (page === 1) {
                setCommits(data.commits);
            } else {
                setCommits((prev) => [...prev, ...data.commits]);
            }
            setHasMore(data.hasMore);
        } catch (err: any) {
            setError(err.message || '加载提交历史失败');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr: string | number) => {
        let date: Date;
        if (typeof dateStr === 'number') {
            date = new Date(dateStr * 1000);
        } else {
            const s = String(dateStr).trim();
            const unixMatch = s.match(/^(\d+)\s*([+-]\d{4})?$/);
            if (unixMatch) {
                date = new Date(parseInt(unixMatch[1], 10) * 1000);
            } else {
                date = new Date(s);
            }
        }
        if (Number.isNaN(date.getTime())) {
            return '未知时间';
        }
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return '今天';
        if (diffDays === 1) return '昨天';
        if (diffDays < 7) return `${diffDays} 天前`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} 周前`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} 个月前`;
        return `${Math.floor(diffDays / 365)} 年前`;
    };

    const getMessageTitle = (message: string) => {
        const lines = message.split('\n');
        return lines[0];
    };

    const getMessageBody = (message: string) => {
        const lines = message.split('\n');
        return lines.slice(1).join('\n').trim();
    };

    const parseCommitDate = (dateStr: string | number) => {
        if (typeof dateStr === 'number') {
            return new Date(dateStr * 1000);
        }
        const s = String(dateStr).trim();
        const unixMatch = s.match(/^(\d+)\s*([+-]\d{4})?$/);
        if (unixMatch) {
            return new Date(parseInt(unixMatch[1], 10) * 1000);
        }
        return new Date(s);
    };

    const formatGroupLabel = (date: Date, count: number) => {
        if (Number.isNaN(date.getTime())) {
            return `未知日期 ${count} 次提交`;
        }
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const targetDay = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const diffDays = Math.round((today - targetDay) / (1000 * 60 * 60 * 24));
        const countText = `${count} 次提交`;

        if (diffDays === 0) return `今天 ${countText}`;
        if (diffDays === 1) return `昨天 ${countText}`;
        return `${date.getMonth() + 1}月${date.getDate()}日, ${date.getFullYear()} ${countText}`;
    };

    const commitGroups = useMemo(() => {
        const sortedCommits = [...commits].sort((a, b) => {
            const aDate = parseCommitDate(a.author.date || a.committer.date).getTime();
            const bDate = parseCommitDate(b.author.date || b.committer.date).getTime();
            return (Number.isNaN(bDate) ? 0 : bDate) - (Number.isNaN(aDate) ? 0 : aDate);
        });

        const groups: Array<{ key: string; date: Date; commits: Commit[] }> = [];
        for (const commit of sortedCommits) {
            const date = parseCommitDate(commit.author.date || commit.committer.date);
            const key = Number.isNaN(date.getTime())
                ? 'unknown'
                : `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
            const group = groups.find((g) => g.key === key);
            if (group) {
                group.commits.push(commit);
            } else {
                groups.push({ key, date, commits: [commit] });
            }
        }
        return groups.map((group) => ({
            ...group,
            label: formatGroupLabel(group.date, group.commits.length),
        }));
    }, [commits]);

    return (
        <div className="commit-history-page">
            <div className="page-header">
                <h1>
                    <Link to={`/${repoPath}`} className="repo-link">
                        {repo}
                    </Link>
                    <span className="separator">/</span>
                    提交历史
                </h1>
                <div className="branch-info">
                    <svg viewBox="0 0 16 16" width="16" height="16">
                        <path
                            fill="currentColor"
                            d="M9.5 3.25a2.25 2.25 0 1 1 3 2.122V6A2.5 2.5 0 0 1 10 8.5H6a1 1 0 0 0-1 1v1.128a2.251 2.251 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.5 0v1.836A2.493 2.493 0 0 1 6 7h4a1 1 0 0 0 1-1v-.628A2.25 2.25 0 0 1 9.5 3.25Z"
                        />
                    </svg>
                    {ref}
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="commit-list">
                {commitGroups.map((group) => (
                    <div key={group.key} className="commit-group">
                        <div className="commit-date-header">{group.label}</div>
                        {group.commits.map((commit) => (
                            <div key={commit.sha} className="commit-item">
                                <div className="commit-avatar">
                                    {commit.author.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="commit-content">
                                    <div className="commit-message">
                                        <Link
                                            to={`/${repoPath}/-/commit/${commit.sha}`}
                                            className="commit-title"
                                        >
                                            {getMessageTitle(commit.message)}
                                        </Link>
                                        {getMessageBody(commit.message) && (
                                            <button className="expand-btn">...</button>
                                        )}
                                    </div>
                                    <div className="commit-meta">
                                        <span className="author-name">{commit.author.name}</span>
                                        <span className="commit-time">
                                            提交于 {formatDate(commit.author.date)}
                                        </span>
                                    </div>
                                </div>
                                <div className="commit-sha">
                                    <Link to={`/${repoPath}/-/commit/${commit.sha}`} className="sha-link">
                                        {commit.sha.substring(0, 7)}
                                    </Link>
                                    <button
                                        className="copy-btn"
                                        onClick={() => navigator.clipboard.writeText(commit.sha)}
                                        title="复制完整 SHA"
                                    >
                                        <svg viewBox="0 0 16 16" width="14" height="14">
                                            <path
                                                fill="currentColor"
                                                d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25Z"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {loading && <div className="loading">加载中...</div>}

            {!loading && hasMore && (
                <div className="load-more">
                    <button onClick={() => setPage((p) => p + 1)} className="load-more-btn">
                        加载更多
                    </button>
                </div>
            )}

            {!loading && commits.length === 0 && (
                <div className="empty-state">
                    <h3>没有提交记录</h3>
                    <p>这个分支还没有任何提交</p>
                </div>
            )}
        </div>
    );
}

export default CommitHistoryPage;
